/**
 * Apply a template to the open document in one pass, as a swap rather than
 * a layer:
 *
 * 1. styles: the document's own definitions of the styles this template
 *    overwrites are kept (once, before the first template), a template
 *    applied earlier has the styles it changed and this one does not define
 *    put back, then this template's style sheet is imported (Word's own
 *    import, overwrite);
 * 2. page setup: margins, orientation, paper, header/footer distance and
 *    the different-first-page / odd-even flags (Word desktop);
 * 3. headers and footers into every section, first-page and even-page ones
 *    included, before any new section exists;
 * 4. pages: the whole template into an empty document, or only its frame
 *    (cover and contents at the start, closing pages at the end) around
 *    existing content, which is never touched. The earlier template's frame
 *    is removed first, by its control or, failing that, by the text it
 *    left; inserted pages go into a fresh paragraph outside any content
 *    control, with a retry without section properties when Word refuses;
 * 5. theme and list definitions, when the template has them;
 * 6. paragraphs left on the old template's custom styles are moved.
 *
 * Nothing here is tracked: it is the frame of the document changing, not
 * its words, and Undo covers it (the result says how many steps). Applying
 * the same template again redoes only the steps that had not succeeded.
 * Each step reports on its own, so the agent can tell the user exactly what
 * happened and what this Word could not do.
 */
import { setTemplateStyleMap } from "./revisionOoxml";
import {
  type AppliedTemplate,
  type StyleSnapshotEntry,
  type TemplateStepStatus,
  loadAppliedTemplate,
  loadStyleSnapshot,
  saveAppliedTemplate,
  saveStyleSnapshot,
  type TemplateStyleMap,
} from "./identity";
import { contentHash } from "./markdown";
import { isOfficeDocumentAvailable } from "./snapshot";
import { diagnoseInserts } from "./insertProbe";
import { type HeaderSnapshot, type PageSetupSnapshot, recordAction } from "./journal";
import { type ExtractInsert, TEMPLATE_FRAME_TAG, insertExtract } from "./templateFrame";
import {
  applyStyleEntriesByHand,
  canImportTemplateStyles,
  importStyleEntries,
  readTemplateStyleEntries,
  snapshotDocumentStyles,
  verifyStyleImport,
} from "./templateStyles";
import { withTrackingOff } from "./tracking";

export { TEMPLATE_FRAME_TAG };

export type TemplatePageSetup = {
  page_width_pt?: number;
  page_height_pt?: number;
  orientation?: "portrait" | "landscape";
  paper?: string;
  margins_pt?: { top?: number; right?: number; bottom?: number; left?: number };
  header_distance_pt?: number;
  footer_distance_pt?: number;
  different_first_page?: boolean;
};

export type ApplyTemplateRequest = {
  template_file_id?: string;
  template_name?: string;
  styles: boolean;
  body: "auto" | "replace" | "frame" | "none";
  /** body=replace on a document with mapped question answers needs this. */
  confirm_replace?: boolean;
  docx_base64: string;
  front_docx_base64?: string;
  /** The cover pages, whole, as a flat OOXML package: the last try if Word refuses the file. */
  front_ooxml?: string;
  middle_docx_base64?: string;
  middle_ooxml?: string;
  back_docx_base64?: string;
  back_ooxml?: string;
  /** The whole template as OOXML (sent when it has no frame pages). */
  docx_ooxml?: string;
  frame: { front: number[]; back: number[] };
  page_count: number;
  header_ooxml?: string | null;
  footer_ooxml?: string | null;
  first_header_ooxml?: string | null;
  first_footer_ooxml?: string | null;
  even_header_ooxml?: string | null;
  even_footer_ooxml?: string | null;
  theme_numbering_ooxml?: string | null;
  page_setup?: TemplatePageSetup | null;
  /** Which styles the template's body uses, for what the add-in writes later. */
  style_map?: TemplateStyleMap;
};

export type StepOutcome = { status: TemplateStepStatus; detail?: string };

export type OrphanStyle = { name: string; paragraphs: number; mapped_to: string; deleted: boolean };

export type ApplyTemplateResult = {
  document_was_empty: boolean;
  body_mode: "replace" | "frame" | "none";
  /** A template applied earlier, whose frame pages were removed first. */
  previous_template: { name: string; file_id: string } | null;
  /** How the earlier template's frame was found: by its control, by its text, or not at all. */
  previous_frame: "tagged" | "untagged" | "none";
  /** The text that was on the removed frame pages (to re-enter a title, say). */
  removed_frame_text: string[];
  /** Styles the earlier template changed that this one does not define, put back to the document's own. */
  restored_styles: string[];
  /** Whether a read-back of an imported style matched the template (null: nothing to check). */
  styles_verified: boolean | null;
  steps: {
    styles: StepOutcome;
    body: StepOutcome;
    page_setup: StepOutcome;
    headers_footers: StepOutcome;
    /** Theme colours/fonts and list definitions, when the template carries them. */
    theme: StepOutcome;
    /** Paragraphs left on a previous template's custom styles, moved onto this template's. */
    restyle: StepOutcome;
  };
  orphan_styles: OrphanStyle[];
  /** How many Undo steps in Word reverse this apply. */
  undo_steps: number;
  /** Plain-words limits the user should hear about. */
  notes: string[];
  /** When the pages step failed: what this host accepts and refuses, attempt by attempt. */
  insert_diagnostics?: {
    host: string;
    platform: string;
    version: string;
    attempts: { name: string; ok: boolean; error?: string; ms: number }[];
  };
};

const supportsDesktop = (set: string): boolean => {
  try {
    return Office.context.requirements.isSetSupported("WordApiDesktop", set);
  } catch {
    return false;
  }
};

const message = (error: unknown): string => {
  const debug = (error as { debugInfo?: { errorLocation?: string } })?.debugInfo?.errorLocation;
  return `${error instanceof Error ? error.message : String(error)}${debug ? ` (${debug})` : ""}`;
};

/** Whether the document has no words, images or tables: a blank page. */
const documentIsEmpty = async (): Promise<boolean> =>
  Word.run(async (context) => {
    const body = context.document.body;
    body.load("text");
    const pictures = body.inlinePictures;
    pictures.load("items");
    const tables = body.tables;
    tables.load("items");
    await context.sync();
    return !body.text.trim() && pictures.items.length === 0 && tables.items.length === 0;
  });

/** How many of the sync feature's question blocks the document holds. */
const questionBlockCount = async (): Promise<number> =>
  Word.run(async (context) => {
    const controls = context.document.body.contentControls;
    controls.load("items/tag");
    await context.sync();
    return controls.items.filter((c) => (c.tag ?? "").startsWith("mytender-q:")).length;
  });

/**
 * Remove the frame pages an earlier template left (their content controls),
 * returning the text that was on them.
 */
const removeTaggedFrame = async (): Promise<string[]> =>
  Word.run(async (context) => {
    const controls = context.document.body.contentControls.getByTag(TEMPLATE_FRAME_TAG);
    controls.load("items,items/text");
    await context.sync();
    const texts = controls.items.map((c) => (c.text ?? "").trim()).filter(Boolean);
    for (const control of controls.items) control.delete(false);
    await context.sync();
    return texts;
  });

const hashOf = (texts: readonly string[]): Promise<string> => contentHash(texts.map((t) => t.trim()).join("\n"));

/**
 * An earlier cover whose control is gone (an older build inserted it untagged,
 * or the writer removed the control): the first paragraphs still carry its
 * text. Found by hash, removed, and its text returned.
 */
const removeUntaggedFrame = async (previous: AppliedTemplate): Promise<string[] | null> => {
  const count = previous.coverParagraphs ?? 0;
  if (!previous.coverHash || count <= 0) return null;
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text");
    await context.sync();
    const head = paragraphs.items.slice(0, count).map((p) => p.text ?? "");
    if (head.length < count || (await hashOf(head)) !== previous.coverHash) return null;
    const first = paragraphs.items[0].getRange(Word.RangeLocation.whole);
    const last = paragraphs.items[count - 1].getRange(Word.RangeLocation.whole);
    first.expandTo(last).delete();
    await context.sync();
    return head.filter((t) => t.trim());
  });
};

/** The text and size of the frame just inserted, to recognise it again. */
const describeInsertedFrame = async (): Promise<{ hash: string; paragraphs: number } | null> =>
  Word.run(async (context) => {
    const controls = context.document.body.contentControls.getByTag(TEMPLATE_FRAME_TAG);
    controls.load("items");
    await context.sync();
    const first = controls.items[0];
    if (!first) return null;
    const paragraphs = first.paragraphs;
    paragraphs.load("items/text");
    await context.sync();
    const texts = paragraphs.items.map((p) => p.text ?? "");
    return { hash: await hashOf(texts), paragraphs: texts.length };
  });

/** The pages step: a full copy into an empty document, or the frame around content. */
const applyPages = async (
  request: ApplyTemplateRequest,
  mode: "replace" | "frame",
  notes: string[]
): Promise<{ outcome: StepOutcome; batches: number }> => {
  const label = `Template: ${request.template_name ?? "frame"}`;
  if (mode === "replace") {
    // A full copy, part by part, with the frame pages marked so a later
    // template can swap them without touching what the user writes in
    // between. Each part goes in as a file, or as the same pages in OOXML
    // if Word refuses the file; the report says which.
    const pieces: { what: string; insert: Omit<ExtractInsert, "where"> }[] =
      request.front_docx_base64 || request.back_docx_base64
        ? [
            ...(request.front_docx_base64
              ? [
                  {
                    what: "cover pages",
                    insert: {
                      docx_base64: request.front_docx_base64,
                      ooxml: request.front_ooxml,
                      label,
                    },
                  },
                ]
              : []),
            ...(request.middle_docx_base64
              ? [
                  {
                    what: "middle pages",
                    insert: {
                      docx_base64: request.middle_docx_base64,
                      label: null,
                    },
                  },
                ]
              : []),
            ...(request.back_docx_base64
              ? [
                  {
                    what: "closing pages",
                    insert: {
                      docx_base64: request.back_docx_base64,
                      ooxml: request.back_ooxml,
                      label,
                    },
                  },
                ]
              : []),
          ]
        : [
            {
              what: "template",
              insert: {
                docx_base64: request.docx_base64,
                ooxml: request.docx_ooxml,
                label: null,
              },
            },
          ];
    await Word.run(async (context) => {
      context.document.body.clear();
      await context.sync();
    });
    let batches = 1;
    const refused: string[] = [];
    for (const piece of pieces) {
      const out = await insertExtract({ ...piece.insert, where: "end" });
      batches += 1;
      if (out.status === "error") {
        refused.push(`${piece.what}: ${out.message ?? "refused"}`);
        continue;
      }
      if (out.degraded) notes.push(`${piece.what}: ${out.message}`);
    }
    if (refused.length) {
      return {
        outcome: {
          status: refused.length === pieces.length ? "error" : "applied",
          detail: `Word refused: ${refused.join(" | ")}${refused.length === pieces.length ? "" : "; the other parts are in"}`,
        },
        batches,
      };
    }
    return {
      outcome: {
        status: "applied",
        detail: `${request.page_count} pages copied; the document is now a copy of the template.`,
      },
      batches,
    };
  }
  const parts: string[] = [];
  const problems: string[] = [];
  let batches = 0;
  if (request.front_docx_base64) {
    const out = await insertExtract({
      docx_base64: request.front_docx_base64,
      ooxml: request.front_ooxml,
      where: "start",
      label,
    });
    batches += 1;
    if (out.status === "inserted") {
      parts.push(`pages ${request.frame.front.join(", ")} at the start${out.degraded ? ` (${out.used} form)` : ""}`);
      if (out.degraded) notes.push(`Cover pages: ${out.message}`);
    } else problems.push(`front pages: ${out.message ?? "refused"}`);
  }
  if (request.back_docx_base64) {
    const out = await insertExtract({
      docx_base64: request.back_docx_base64,
      ooxml: request.back_ooxml,
      where: "end",
      label,
    });
    batches += 1;
    if (out.status === "inserted") {
      parts.push(`pages ${request.frame.back.join(", ")} at the end${out.degraded ? ` (${out.used} form)` : ""}`);
      if (out.degraded) notes.push(`Closing pages: ${out.message}`);
    } else problems.push(`closing pages: ${out.message ?? "refused"}`);
  }
  if (!parts.length && !problems.length) {
    return {
      outcome: { status: "skipped", detail: "The template has no cover, contents or closing pages to add." },
      batches,
    };
  }
  if (problems.length) {
    return {
      outcome: {
        status: parts.length ? "applied" : "error",
        detail: `${parts.length ? `Template ${parts.join(" and ")}. ` : ""}Word refused: ${problems.join("; ")}.`,
      },
      batches,
    };
  }
  return {
    outcome: { status: "applied", detail: `Template ${parts.join(" and ")}; existing content untouched.` },
    batches,
  };
};

/**
 * After a new style sheet lands, paragraphs still on styles the new template
 * does not define (a previous template's "Acme Body", say) would keep the
 * old look. Move them to the closest built-in the new template styles:
 * outline level to the matching heading, list items to List Paragraph, the
 * rest to Normal, then delete those orphaned custom styles so the document
 * carries no trace of the old template.
 */
const restyleOrphans = async (
  templateStyleNames: readonly string[]
): Promise<{ outcome: StepOutcome; orphans: OrphanStyle[] }> =>
  Word.run(async (context) => {
    const keep = new Set(templateStyleNames.map((n) => n.toLowerCase()));
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/style,items/styleBuiltIn,items/outlineLevel,items/isListItem");
    await context.sync();

    const counts = new Map<string, { paragraphs: number; mapped_to: string }>();
    for (const p of paragraphs.items) {
      const name = p.style ?? "";
      const custom = String(p.styleBuiltIn) === "Other";
      if (!custom || !name || keep.has(name.toLowerCase())) continue;
      const level = typeof p.outlineLevel === "number" ? p.outlineLevel : 0;
      const target = level >= 1 && level <= 9 ? `Heading ${level}` : p.isListItem ? "List Paragraph" : "Normal";
      p.style = target;
      const entry = counts.get(name) ?? { paragraphs: 0, mapped_to: target };
      entry.paragraphs += 1;
      counts.set(name, entry);
    }
    await context.sync();

    // Drop the orphaned custom styles themselves (WordApi 1.5) so the old
    // template leaves nothing behind in the style gallery.
    const orphans: OrphanStyle[] = [];
    let canDelete = false;
    try {
      canDelete = Office.context.requirements.isSetSupported("WordApi", "1.5");
    } catch {
      canDelete = false;
    }
    if (canDelete) {
      const styles = context.document.getStyles();
      styles.load("items/nameLocal,items/builtIn,items/inUse");
      await context.sync();
      const stale = styles.items.filter(
        (st) => !st.builtIn && !keep.has(st.nameLocal.toLowerCase()) && (counts.has(st.nameLocal) || !st.inUse)
      );
      for (const st of stale) {
        try {
          st.delete();
        } catch {
          // a linked or table style Word refuses to drop; harmless
        }
      }
      await context.sync();
      const deleted = new Set(stale.map((st) => st.nameLocal));
      for (const [name, entry] of counts) orphans.push({ name, ...entry, deleted: deleted.has(name) });
      for (const name of deleted)
        if (!counts.has(name)) orphans.push({ name, paragraphs: 0, mapped_to: "", deleted: true });
    } else {
      for (const [name, entry] of counts) orphans.push({ name, ...entry, deleted: false });
    }

    if (!orphans.length)
      return { outcome: { status: "skipped", detail: "No paragraphs were left on old styles." }, orphans };
    const moved = orphans.filter((o) => o.paragraphs > 0);
    const removed = orphans.filter((o) => o.deleted).length;
    return {
      outcome: {
        status: "applied",
        detail: `${moved.reduce((n, o) => n + o.paragraphs, 0)} paragraphs moved from ${moved.length} old style(s) (${moved
          .map((o) => `${o.name} → ${o.mapped_to}`)
          .join(", ")}); ${removed} old custom style(s) removed.`,
      },
      orphans,
    };
  });

const applyPageSetup = async (setup: TemplatePageSetup, evenPages: boolean): Promise<StepOutcome> => {
  if (!supportsDesktop("1.3")) {
    const m = setup.margins_pt;
    const margins = m
      ? `top ${m.top ?? "?"}, bottom ${m.bottom ?? "?"}, left ${m.left ?? "?"}, right ${m.right ?? "?"} pt`
      : "unknown";
    return {
      status: "unsupported",
      detail: `Page setup cannot be changed by the add-in in this Word (Word on the web). Template: ${setup.paper ?? "custom size"}, ${setup.orientation ?? "portrait"}, margins ${margins}. Set them in Layout > Margins.`,
    };
  }
  return Word.run(async (context) => {
    const page = context.document.pageSetup;
    const m = setup.margins_pt ?? {};
    if (typeof m.top === "number") page.topMargin = m.top;
    if (typeof m.bottom === "number") page.bottomMargin = m.bottom;
    if (typeof m.left === "number") page.leftMargin = m.left;
    if (typeof m.right === "number") page.rightMargin = m.right;
    if (typeof setup.header_distance_pt === "number") page.headerDistance = setup.header_distance_pt;
    if (typeof setup.footer_distance_pt === "number") page.footerDistance = setup.footer_distance_pt;
    if (setup.orientation) page.orientation = setup.orientation === "landscape" ? "Landscape" : "Portrait";
    if (typeof setup.page_width_pt === "number" && typeof setup.page_height_pt === "number") {
      page.pageWidth = setup.page_width_pt;
      page.pageHeight = setup.page_height_pt;
    }
    // The flags before the headers: a first-page or even-page header only
    // shows once its flag is on.
    if (typeof setup.different_first_page === "boolean")
      page.differentFirstPageHeaderFooter = setup.different_first_page;
    if (evenPages) page.oddAndEvenPagesHeaderFooter = true;
    await context.sync();
    return {
      status: "applied",
      detail: `${setup.paper ?? "custom size"}, ${setup.orientation ?? "portrait"}, margins applied.`,
    };
  });
};

const applyHeadersFooters = async (request: ApplyTemplateRequest): Promise<StepOutcome> =>
  Word.run(async (context) => {
    const sections = context.document.sections;
    sections.load("items");
    await context.sync();
    const done: string[] = [];
    for (const section of sections.items) {
      if (request.header_ooxml) section.getHeader("Primary").insertOoxml(request.header_ooxml, "Replace");
      if (request.footer_ooxml) section.getFooter("Primary").insertOoxml(request.footer_ooxml, "Replace");
      if (request.first_header_ooxml) section.getHeader("FirstPage").insertOoxml(request.first_header_ooxml, "Replace");
      if (request.first_footer_ooxml) section.getFooter("FirstPage").insertOoxml(request.first_footer_ooxml, "Replace");
      if (request.even_header_ooxml) section.getHeader("EvenPages").insertOoxml(request.even_header_ooxml, "Replace");
      if (request.even_footer_ooxml) section.getFooter("EvenPages").insertOoxml(request.even_footer_ooxml, "Replace");
    }
    if (request.header_ooxml) done.push("header");
    if (request.footer_ooxml) done.push("footer");
    if (request.first_header_ooxml || request.first_footer_ooxml) done.push("first-page header/footer");
    if (request.even_header_ooxml || request.even_footer_ooxml) done.push("even-page header/footer");
    if (!done.length) return { status: "skipped", detail: "The template has no header or footer." };
    await context.sync();
    return { status: "applied", detail: `${done.join(", ")} applied to ${sections.items.length} section(s).` };
  });

/**
 * Theme colours/fonts and list definitions: Word adopts the parts of an
 * inserted package. The package holds one empty paragraph, removed again.
 */
const applyThemeAndNumbering = async (pkg: string): Promise<StepOutcome> =>
  Word.run(async (context) => {
    const body = context.document.body;
    const inserted = body.insertOoxml(pkg, Word.InsertLocation.end);
    await context.sync();
    const paragraphs = inserted.paragraphs;
    paragraphs.load("items/text");
    await context.sync();
    for (const p of paragraphs.items) if (!(p.text ?? "").trim()) p.delete();
    await context.sync();
    return {
      status: "applied",
      detail:
        "Theme colours/fonts and list definitions offered to Word (attempted: Word decides whether it adopts a theme from inserted content; check a heading's colour and a list's bullets).",
    };
  });

const hasHeaderParts = (request: ApplyTemplateRequest): boolean =>
  Boolean(
    request.header_ooxml ||
      request.footer_ooxml ||
      request.first_header_ooxml ||
      request.first_footer_ooxml ||
      request.even_header_ooxml ||
      request.even_footer_ooxml
  );

/**
 * Run the steps and report each. Runs with change tracking off.
 * @param request - The template parts, built server-side.
 */
export const applyTemplate = async (request: ApplyTemplateRequest): Promise<ApplyTemplateResult> => {
  if (!isOfficeDocumentAvailable()) throw new Error("The document isn't available.");
  return withTrackingOff(() => applySteps(request));
};

/** The headers and footers a template is about to replace, per section, for undo. */
const snapshotHeaders = async (): Promise<HeaderSnapshot[]> =>
  Word.run(async (context) => {
    const sections = context.document.sections;
    sections.load("items");
    await context.sync();
    const reads = sections.items.map((section) => ({
      primaryHeader: section.getHeader("Primary").getOoxml(),
      primaryFooter: section.getFooter("Primary").getOoxml(),
      firstHeader: section.getHeader("FirstPage").getOoxml(),
      firstFooter: section.getFooter("FirstPage").getOoxml(),
      evenHeader: section.getHeader("EvenPages").getOoxml(),
      evenFooter: section.getFooter("EvenPages").getOoxml(),
    }));
    await context.sync();
    return reads.map((r, section) => ({
      section,
      primaryHeader: r.primaryHeader.value,
      primaryFooter: r.primaryFooter.value,
      firstHeader: r.firstHeader.value,
      firstFooter: r.firstFooter.value,
      evenHeader: r.evenHeader.value,
      evenFooter: r.evenFooter.value,
    }));
  });

/** The page setup a template is about to change, for undo (Word desktop only). */
const snapshotPageSetup = async (): Promise<PageSetupSnapshot | null> => {
  if (!supportsDesktop("1.3")) return null;
  return Word.run(async (context) => {
    const page = context.document.pageSetup;
    page.load(
      "topMargin,bottomMargin,leftMargin,rightMargin,headerDistance,footerDistance,orientation,pageWidth,pageHeight,differentFirstPageHeaderFooter,oddAndEvenPagesHeaderFooter"
    );
    await context.sync();
    return {
      topMargin: page.topMargin,
      bottomMargin: page.bottomMargin,
      leftMargin: page.leftMargin,
      rightMargin: page.rightMargin,
      headerDistance: page.headerDistance,
      footerDistance: page.footerDistance,
      orientation: String(page.orientation),
      pageWidth: page.pageWidth,
      pageHeight: page.pageHeight,
      differentFirstPageHeaderFooter: page.differentFirstPageHeaderFooter,
      oddAndEvenPagesHeaderFooter: page.oddAndEvenPagesHeaderFooter,
    };
  });
};

const applySteps = async (request: ApplyTemplateRequest): Promise<ApplyTemplateResult> => {
  const notes: string[] = [];
  let undoSteps = 0;
  const startedAt = new Date().toISOString();
  // What undo_last_change needs to take this apply back.
  let stylesBeforeApply: StyleSnapshotEntry[] = [];
  let headersBeforeApply: HeaderSnapshot[] = [];
  let pageSetupBeforeApply: PageSetupSnapshot | null = null;
  const skipped = (detail: string): StepOutcome => ({ status: "skipped", detail });
  const run = async (step: () => Promise<StepOutcome>): Promise<StepOutcome> => {
    try {
      const outcome = await step();
      if (outcome.status === "applied") undoSteps += 1;
      return outcome;
    } catch (error) {
      return { status: "error", detail: message(error) };
    }
  };

  const empty = await documentIsEmpty();
  const blocks = empty ? 0 : await questionBlockCount();
  let bodyMode: "replace" | "frame" | "none" =
    request.body === "none" ? "none" : request.body === "auto" ? (empty ? "replace" : "frame") : request.body;
  let bodyRefused: StepOutcome | null = null;
  if (bodyMode === "replace" && blocks > 0 && !request.confirm_replace) {
    // The body holds mapped question answers: replacing it would remove them
    // and their link to mytender. Only on the user's explicit word.
    bodyRefused = {
      status: "skipped",
      detail: `Not replaced: the document holds ${blocks} mapped question answer(s), which body=replace would remove. Ask the user; then call again with body=replace and confirm_replace=true.`,
    };
    bodyMode = "none";
  }

  const previous = await loadAppliedTemplate();
  const sameTemplate = Boolean(previous && request.template_file_id && previous.fileId === request.template_file_id);
  const alreadyDone = (step: keyof NonNullable<AppliedTemplate["steps"]>): boolean =>
    sameTemplate && previous?.steps?.[step] === "applied";
  const again = (step: string): StepOutcome =>
    skipped(`Already applied by this template on ${previous?.appliedAt ?? "an earlier apply"}; ${step} left as is.`);

  // The earlier template's frame goes before anything else lands.
  let removedFrameText: string[] = [];
  let previousFrame: ApplyTemplateResult["previous_frame"] = "none";
  if (bodyMode !== "none" && !alreadyDone("body")) {
    try {
      removedFrameText = await removeTaggedFrame();
      if (removedFrameText.length) previousFrame = "tagged";
      else if (previous) {
        const untagged = await removeUntaggedFrame(previous);
        if (untagged) {
          removedFrameText = untagged;
          previousFrame = "untagged";
        }
      }
      if (previousFrame !== "none") undoSteps += 1;
    } catch (error) {
      console.warn("Could not remove the previous template's frame", error);
    }
  }

  // Styles: restore what the earlier template changed and this one does not
  // define, keep the document's own definitions of what this one overwrites,
  // then import.
  let templateStyleNames: string[] | null = null;
  let restoredStyles: string[] = [];
  let stylesVerified: boolean | null = null;
  const styles = !request.styles
    ? skipped("Not requested.")
    : alreadyDone("styles")
      ? again("styles")
      : await run(async () => {
          if (!canImportTemplateStyles())
            return { status: "unsupported", detail: "This Word cannot import styles (needs WordApi 1.6)." };
          const entries = await readTemplateStyleEntries(request.docx_base64);
          if (!entries.length) return { status: "error", detail: "The template has no paragraph or character styles." };
          const names = entries.map((e) => String(e.nameLocal ?? "")).filter(Boolean);
          const lower = new Set(names.map((n) => n.toLowerCase()));
          const snapshot = await loadStyleSnapshot();
          const known = new Set(snapshot.map((e) => String(e.nameLocal).toLowerCase()));
          // Put back the styles the earlier template changed that this one leaves alone.
          if (previous && !sameTemplate && previous.styleNames?.length) {
            const toRestore = snapshot.filter((e) => {
              const name = String(e.nameLocal).toLowerCase();
              return !lower.has(name) && previous.styleNames?.some((n) => n.toLowerCase() === name);
            });
            if (toRestore.length) {
              await importStyleEntries(toRestore);
              restoredStyles = toRestore.map((e) => String(e.nameLocal));
            }
          }
          // Everything this template overwrites, for undo; the persistent
          // snapshot below keeps only what no template has touched before.
          stylesBeforeApply = (await snapshotDocumentStyles(names)) as StyleSnapshotEntry[];
          // Capture the document's own definitions before they are overwritten (first time only).
          const fresh = await snapshotDocumentStyles(names.filter((n) => !known.has(n.toLowerCase())));
          if (fresh.length)
            await saveStyleSnapshot([...snapshot, ...(fresh as StyleSnapshotEntry[])]).catch(() => undefined);
          const imported = await importStyleEntries(entries);
          templateStyleNames = names;
          stylesVerified = await verifyStyleImport(entries).catch(() => null);
          // Word's import reported 137 styles but the read-back disagreed
          // (30 Sep, Word for Mac): whatever it left untouched is set by
          // hand from the same entries, style by style.
          let byHand = "";
          if (stylesVerified === false) {
            const hand = await applyStyleEntriesByHand(entries).catch(() => null);
            if (hand) {
              stylesVerified = await verifyStyleImport(entries).catch(() => null);
              byHand = ` ${hand.set.length} set by hand${hand.added.length ? ` (${hand.added.length} added)` : ""}${hand.failed.length ? `; ${hand.failed.length} refused: ${hand.failed.slice(0, 3).join(", ")}` : ""}.`;
            }
          }
          const count = imported.length || (stylesVerified ? entries.length : 0);
          const verdict = stylesVerified === false ? " Read-back still did not match." : "";
          return {
            status: stylesVerified === false && !imported.length ? "error" : "applied",
            detail: `${count || entries.length} styles imported${restoredStyles.length ? `; ${restoredStyles.length} restored from before the earlier template` : ""}.${byHand}${verdict}`,
          };
        });

  if (request.page_setup && !alreadyDone("page_setup"))
    pageSetupBeforeApply = await snapshotPageSetup().catch(() => null);
  const pageSetup = !request.page_setup
    ? skipped("Not requested.")
    : alreadyDone("page_setup")
      ? again("page setup")
      : await run(() =>
          applyPageSetup(
            request.page_setup as TemplatePageSetup,
            Boolean(request.even_header_ooxml || request.even_footer_ooxml)
          )
        );

  // Headers and footers before the pages: the cover's own section keeps its own.
  if (hasHeaderParts(request) && !alreadyDone("headers_footers"))
    headersBeforeApply = await snapshotHeaders().catch(() => []);
  const headersFooters = !hasHeaderParts(request)
    ? skipped(request.header_ooxml === undefined ? "Not requested." : "The template has no header or footer.")
    : alreadyDone("headers_footers")
      ? again("headers and footers")
      : await run(() => applyHeadersFooters(request));

  let body: StepOutcome;
  if (bodyRefused) body = bodyRefused;
  else if (bodyMode === "none") body = skipped("Not requested.");
  else if (alreadyDone("body")) body = again("pages");
  else {
    try {
      const pages = await applyPages(request, bodyMode, notes);
      body = pages.outcome;
      undoSteps += pages.batches;
    } catch (error) {
      body = { status: "error", detail: message(error) };
    }
  }

  // A refused cover is a mystery worth one more minute: try the other insert
  // paths now, with the parts already in hand, and report what Word said.
  const insertDiagnostics =
    body.status === "error" && bodyMode !== "none"
      ? await diagnoseInserts({
          docx_base64: request.docx_base64,
          front_docx_base64: request.front_docx_base64,
          front_ooxml: request.front_ooxml ?? request.docx_ooxml,
        }).catch(() => undefined)
      : undefined;

  const theme = request.theme_numbering_ooxml
    ? await run(() => applyThemeAndNumbering(request.theme_numbering_ooxml as string))
    : skipped("The template carries no theme or list definitions.");

  let restyle: StepOutcome = skipped("Styles were not imported.");
  let orphans: OrphanStyle[] = [];
  if (templateStyleNames) {
    try {
      const result = await restyleOrphans(templateStyleNames);
      restyle = result.outcome;
      orphans = result.orphans;
      if (restyle.status === "applied") undoSteps += 1;
    } catch (error) {
      restyle = { status: "error", detail: message(error) };
    }
  }

  notes.push("Not carried over: table styles, watermarks and page colour.");
  if (pageSetup.status === "unsupported") notes.push("Page setup could not be changed in this Word.");

  if (request.template_file_id) {
    const frame = body.status === "applied" ? await describeInsertedFrame().catch(() => null) : null;
    const steps: NonNullable<AppliedTemplate["steps"]> = {
      styles: sameTemplate && previous?.steps?.styles === "applied" ? "applied" : styles.status,
      body: sameTemplate && previous?.steps?.body === "applied" ? "applied" : body.status,
      page_setup: sameTemplate && previous?.steps?.page_setup === "applied" ? "applied" : pageSetup.status,
      headers_footers:
        sameTemplate && previous?.steps?.headers_footers === "applied" ? "applied" : headersFooters.status,
    };
    await saveAppliedTemplate({
      fileId: request.template_file_id,
      name: request.template_name ?? "template",
      appliedAt: new Date().toISOString(),
      steps,
      styleNames: templateStyleNames ?? (sameTemplate ? previous?.styleNames : undefined),
      styleMap: request.style_map ?? (sameTemplate ? previous?.styleMap : undefined),
      ...(frame && { coverHash: frame.hash, coverParagraphs: frame.paragraphs }),
      ...(!frame &&
        sameTemplate &&
        previous?.coverHash && { coverHash: previous.coverHash, coverParagraphs: previous.coverParagraphs }),
    }).catch(() => undefined);
    setTemplateStyleMap(request.style_map ?? (sameTemplate ? previous?.styleMap : undefined));
  }
  const changed = [styles, body, pageSetup, headersFooters, theme].some((step) => step.status === "applied");
  if (changed) {
    await recordAction({
      kind: "template",
      at: startedAt,
      summary: `Template ${request.template_name ?? ""}`.trim(),
      template: {
        frameTag: TEMPLATE_FRAME_TAG,
        stylesBefore: stylesBeforeApply,
        headersBefore: headersBeforeApply,
        pageSetupBefore: pageSetupBeforeApply,
        themeAttempted: theme.status === "applied",
      },
    });
  }
  return {
    document_was_empty: empty,
    body_mode: bodyMode,
    previous_template: previous ? { name: previous.name, file_id: previous.fileId } : null,
    previous_frame: previousFrame,
    removed_frame_text: removedFrameText,
    restored_styles: restoredStyles,
    styles_verified: stylesVerified,
    steps: { styles, body, page_setup: pageSetup, headers_footers: headersFooters, theme, restyle },
    orphan_styles: orphans,
    undo_steps: undoSteps,
    notes,
    ...(insertDiagnostics && { insert_diagnostics: insertDiagnostics }),
  };
};
